import { Application, LifeCycles, registerApplication, RegisterApplicationConfig, start } from 'single-spa';
import { constructApplications, constructLayoutEngine, constructRoutes } from 'single-spa-layout';
import singleSpaReact from 'single-spa-react';
import React from 'react';
import ReactDOMClient from 'react-dom/client';
import { QuantumReduxProvider } from '@quantum/tools/redux';
import { BrowserRouter } from 'react-router-dom';
import { QuantumProviderValue } from '@quantum/tools/context';
import { ModelManager } from '@adobe/aem-spa-page-model-manager';
import store, { rootReducer } from '../redux/store';
import { onAnalyticsEvent } from '../../utils/analytics';
import { REMOTE_APPLICATIONS } from '../utils/constants';
import { UCL } from '../../modules/remote-ucl/types/UCL';
import { loadUcl } from '../../modules/remote-ucl/client';
import uclApp from '../../assembler-react-ui/src/modules/App/UCLApp';
import { QuantumProviderWrapper } from '../../assembler-react-ui/src/components/QuantumProviderWrapper';
import { Marker } from '../types/Marker';
import { loadMfe } from '../../modules/mfe-ssr/client';

ModelManager.initializeAsync({ model: { ':path': window.location.pathname }, path: window.location.pathname });

const loadRemoteApp = (marker: Marker) => {
  return loadMfe(marker).catch((err) => {
    console.error(err);
    return singleSpaReact({ React, ReactDOMClient, rootComponent: () => <div></div> });
  }) as Promise<Application>;
};

export const buildApplications = (routes?: unknown) => {
  if (!routes) {
    return [];
  }
  return constructApplications({
    routes: routes,
    loadApp: async ({ name }) => {
      const { uclVersion } = window.singleSpaLayoutData.props[name] as {
        uclVersion: string;
      };

      const importRemotes: Promise<Application | UCL | null>[] = [];
      if (name === REMOTE_APPLICATIONS.FORD_CREDIT) {
        importRemotes.push(Promise.resolve(uclApp));
      } else {
        importRemotes.push(loadRemoteApp(window.singleSpaLayoutData.props[name] as Marker));
      }

      importRemotes.push(loadUcl(uclVersion));

      const [app, remoteUcl] = (await Promise.all(importRemotes)) as [Application, UCL];

      window.singleSpaLayoutData.props[name].remoteUcl = remoteUcl;
      const { AnalyticsContext } = remoteUcl;

      if ((app as LifeCycles).bootstrap) {
        return app;
      } else {
        const Component = app as unknown as React.FC;
        const mfeProps = window.singleSpaLayoutData.props[name];
        const { language, brand, country, deviceType, enabledSSR, productName } = mfeProps;
        return singleSpaReact({
          React,
          ReactDOMClient,
          rootComponent: () => (
            <BrowserRouter>
              <QuantumProviderWrapper
                value={{ brand, locale: `${language}-${country}`, deviceType, tenant: productName } as QuantumProviderValue}>
                <QuantumReduxProvider store={store}>
                  <AnalyticsContext.Provider value={{ onAnalyticsEvent }}>
                    <Component {...mfeProps} />
                  </AnalyticsContext.Provider>
                </QuantumReduxProvider>
              </QuantumProviderWrapper>
            </BrowserRouter>
          ),
          renderType: enabledSSR ? 'hydrateRoot' : 'createRoot'
        });
      }
    }
  });
};

// This is the containing element for the micro frontends
const appLayoutElement = document.querySelector('#app-layout');

const routes = appLayoutElement ? constructRoutes(appLayoutElement) : undefined;

const isBuiltInApplication = (app: RegisterApplicationConfig) => !app.name.startsWith('mfe');

if (routes) {
  let applications = buildApplications(routes);
  applications = applications.filter(
    ({ name }) =>
      name !== REMOTE_APPLICATIONS.FORD_CREDIT_HEADER &&
      name !== REMOTE_APPLICATIONS.FORD_CREDIT_FOOTER &&
      name !== REMOTE_APPLICATIONS.FORD_PRO_HEADER &&
      name !== REMOTE_APPLICATIONS.FORD_PRO_FOOTER
  );
  // ensure assembler application load first
  const sortedApplications = window.assemblerError ? [] : applications.sort((a) => (isBuiltInApplication(a) ? -1 : 1));

  sortedApplications.forEach((app) => {
    const customPropsWithStore = (...args: string[]) => {
      // eslint-disable-next-line @typescript-eslint/ban-types
      return { ...(app.customProps as Function)(args), store: { store }, rootReducer: { rootReducer } };
    };

    registerApplication({ ...app, customProps: customPropsWithStore });
  });

  constructLayoutEngine({ routes, applications: sortedApplications });
}

start();
